import { Component } from '@theme/component';

/**
 * A custom element that displays Zinrelo rewards points information.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} pointsDisplay - The element that displays the points value.
 * @extends {Component<Refs>}
 */
export class RewardsPointsComponent extends Component {
  requiredRefs = [
    'pointsDisplay',
    'pointsNeeded',
    'pointsAwayMessage',
    'eligibleMessage',
    'pointsText',
    'pointsLabel',
    'pointsScreenReader',
    'blockLoader',
  ];

  connectedCallback() {
    super.connectedCallback();
    this.waitForZinreloAndRequestData();
  }

  /**
   * Get the available rewards points from Zinrelo data
   *
   * @returns {number} The number of available points
   */
  get rewardsPoints() {
    return this._userData?.available_points || 0;
  }

  /**
   * Get the formatted rewards points for display
   *
   * @returns {string} The formatted number of available points
   */
  get formattedRewardsPoints() {
    return this.rewardsPoints.toLocaleString();
  }

  /**
   * Get the next reward points count from block settings
   *
   * @returns {number} The next reward points count
   */
  get nextRewardPointsCount() {
    const setting = this.dataset.nextRewardPointsCount;
    return setting ? parseInt(setting, 10) : 1500;
  }

  /**
   * Update the rewards display with current data
   */
  updateRewardsDisplay() {
    if (this.refs.blockLoader) {
      this.refs.blockLoader.classList.add('hidden');
    }

    if (this.refs.pointsDisplay) {
      if (this.refs.pointsText) {
        this.refs.pointsText.textContent = this.formattedRewardsPoints;
        this.refs.pointsText.classList.remove('hidden');
      }
    }

    if (this.refs.pointsLabel) {
      this.refs.pointsLabel.classList.remove('hidden');
    }

    if (this.refs.pointsScreenReader && this.refs.pointsText && this.refs.pointsLabel) {
      this.refs.pointsScreenReader.textContent = `${this.refs.pointsText.textContent} ${this.refs.pointsLabel.textContent}`;
    }

    this.updateRewardMessage();
  }

  /**
   * Update the reward message based on current points vs next reward
   */
  updateRewardMessage() {
    const currentPoints = this.rewardsPoints;
    const nextRewardPoints = this.nextRewardPointsCount;

    if (currentPoints < nextRewardPoints) {
      const pointsNeeded = nextRewardPoints - currentPoints;

      if (this.refs.pointsNeeded) {
        this.refs.pointsNeeded.textContent = pointsNeeded.toLocaleString();
      }

      if (this.refs.pointsAwayMessage) {
        this.refs.pointsAwayMessage.classList.remove('hidden');
      }
    } else {
      if (this.refs.eligibleMessage) {
        this.refs.eligibleMessage.classList.remove('hidden');
      }
    }
  }

  waitForZinreloAndRequestData() {
    let attempts = 0;
    const maxAttempts = 100;

    const checkZinreloReady = () => {
      attempts++;
      const zrlMi = /** @type {any} */ (window).zrl_mi;
      const userData = zrlMi?.authenticate_user_resp?.user_data;

      if (userData) {
        this._userData = {
          available_points: userData.available_points,
          loyalty_tier_name: userData.loyalty_level_name,
        };
        this.updateRewardsDisplay();

        return;
      }

      if (attempts >= maxAttempts) {
        console.warn('Zinrelo data not available after timeout');
        this.updateRewardsDisplay();

        return;
      }

      setTimeout(checkZinreloReady, 100);
    };

    checkZinreloReady();
  }
}

if (!customElements.get('rewards-points-component')) {
  customElements.define('rewards-points-component', RewardsPointsComponent);
}
